import { createSlice } from '@reduxjs/toolkit'

import {
  getEcomOrderAPI,
  postEcomOrderAPI,
  postShopOrderProductImei,
  postEcomOrderStatusAPI,
  sendEcomOrderSmsAPI,
} from '@/api/shopApi'
import { ECOM_STATUS } from '@/util/orders'

import { setApiErrorPopup } from './popupReducer'

export const NAME = 'ecom-order'

export const fetchEcomOrder =
  (orderId: any, overridePendingCheck = false, callBack?: () => void) =>
  // @ts-ignore
  async (dispatch, getState) => {
    const isLoading = getIsLoading(getState())

    if (isLoading && !overridePendingCheck) return

    if (orderId !== getOrderId(getState())) {
      dispatch(reset())
    }

    dispatch(pending(orderId))

    try {
      // @ts-ignore
      const { order, products, sms_log } = await getEcomOrderAPI(orderId)

      dispatch(success({ order, products, sms_log }))
      callBack?.()
    } catch (error) {
      dispatch(setApiErrorPopup('Could not load order'))
      dispatch(failure('Could not load order'))
    }
  }

// @ts-ignore
export const setEcomOrderStatus = (statusId, callBack) => async (dispatch, getState) => {
  const isLoading = getIsLoading(getState())
  const orderId = getOrderId(getState())
  if (isLoading) return

  dispatch(pending(orderId))

  try {
    const payload = getOrderPayload(getState())
    await postEcomOrderStatusAPI({ orderId, statusId, payload })
    await dispatch(fetchEcomOrder(orderId, true, callBack?.()))
  } catch (error) {
    dispatch(setApiErrorPopup('Could not update order'))
    dispatch(failure('Could not update order'))
  }
}

// @ts-ignore
export const saveEcomOrder = () => async (dispatch, getState) => {
  const isLoading = getIsLoading(getState())
  const orderId = getOrderId(getState())

  if (isLoading) return

  dispatch(pending(orderId))

  try {
    const payload = getOrderPayload(getState())

    await postEcomOrderAPI({ orderId, payload })
    await dispatch(fetchEcomOrder(orderId, true))
  } catch (error) {
    dispatch(setApiErrorPopup('Could not update order'))
    dispatch(failure('Could not update order'))
  }
}

export const postImei =
  // @ts-ignore


    ({ imei, productId, onSuccess }) =>
    // @ts-ignore
    async (dispatch, getState) => {
      const orderId = getOrderId(getState())

      try {
        await postShopOrderProductImei({ orderId, imei, orderLineId: productId })
        await dispatch(fetchEcomOrder(orderId))
        onSuccess()
      } catch (error) {
        dispatch(setApiErrorPopup('Could not update imei'))
        dispatch(failure('Could not update imei'))
      }
    }
// @ts-ignore
export const sendEcomOrderSms = () => async (dispatch, getState) => {
  const isLoading = getIsLoading(getState())

  if (isLoading) return

  const order = getEcomOrder(getState())

  dispatch(pending(order.id))

  try {
    const products = getEcomOrderProducts(getState())
    const aliasId = getSelectedAlias(getState())
    const message = getSmsInputText(getState())
    const receiver = getSelectedReceiver(getState())

    if (receiver !== 'end_user' && receiver !== 'order_user') {
      dispatch(setApiErrorPopup('Invalid SMS receiver selected'))
      dispatch(failure('Could not send sms'))
      return
    }
    const endUserId = receiver === 'end_user' ? getEcomOrderEndUserId(getState()) : undefined
    const createdByUserId =
      receiver === 'order_user' ? getEcomOrder(getState())?.created_by_user_id : undefined
    const sms_log = await sendEcomOrderSmsAPI({
      orderId: order.id,
      aliasId,
      end_user_receiver: endUserId,
      order_user_receiver: createdByUserId,
      message,
    })

    dispatch(
      success({
        order,
        sms_log,
        products,
      })
    )
    dispatch(setSmsInputText(''))
  } catch (error) {
    dispatch(setApiErrorPopup('Could not send sms'))
    dispatch(failure('Could not send sms'))
  }
}

const initialState = {
  isLoading: false,
  isLoaded: false,

  error: undefined,
  orderId: undefined,

  order: {},
  products: [],
  sms_log: [],

  smsInputText: '',
  selectedAlias: 0,
  selectedReceiver: '',
}

const { actions, reducer } = createSlice({
  name: NAME,
  initialState,
  reducers: {
    pending: (state, { payload: orderId }) => {
      state.isLoading = true
      state.error = undefined
      state.orderId = orderId
    },
    success: (state, { payload }) => {
      const { order, products, sms_log } = payload
      state.isLoading = false
      state.isLoaded = true
      state.order = order
      state.products = products
      state.sms_log = sms_log
    },
    failure: (state, { payload: error }) => {
      state.isLoading = false
      state.isLoaded = true
      state.error = error
    },
    setDeliveryDate: (state, { payload: deliveryDate }) => {
      state.order = {
        ...state.order,
        delivery_date: deliveryDate,
      }
    },
    setDealerComment: (state, { payload: dealerComment }) => {
      state.order = {
        ...state.order,
        dealer_comment: dealerComment,
      }
    },
    setInternalComment: (state, { payload: internalComment }) => {
      state.order = {
        ...state.order,
        internal_comment: internalComment,
      }
    },
    setVendorSalesOrderNo: (state, { payload: vendorSalesOrderNo }) => {
      state.order = {
        ...state.order,
        vendor_sales_order_no: vendorSalesOrderNo,
      }
    },
    setSmsInputText: (state, { payload: text }) => {
      state.smsInputText = text
    },
    setAlias: (state, { payload: alias }) => {
      state.selectedAlias = alias
    },
    setSelectedReceiver: (state, { payload: receiver }) => {
      state.selectedReceiver = receiver
    },
    reset: () => ({
      ...initialState,
    }),
  },
})

export const {
  pending,
  success,
  failure,
  setDealerComment,
  setInternalComment,
  setVendorSalesOrderNo,
  setDeliveryDate,
  setSmsInputText,
  setAlias,
  setSelectedReceiver,
  reset,
  // @ts-ignore
  setImei,
} = actions
export default reducer

const getState = (state: any) => state[NAME]

export const getIsLoading = (state: any) => getState(state).isLoading
export const getIsLoaded = (state: any, orderId: any) =>
  getState(state).isLoaded && (getOrderId(state) === orderId || !orderId)
export const getError = (state: any) => getState(state).error

export const getOrderId = (state: any) => getState(state).orderId

export const getEcomOrder = (state: any) => getState(state).order || {}
export const getEcomOrderProducts = (state: any) => getState(state).products || []
export const getEcomOrderSmsLog = (state: any) => getState(state).sms_log || []

export const getEcomOrderStatus = (state: any) => getEcomOrder(state).status
export const getEcomOrderIsOpen = (state: any) =>
  [ECOM_STATUS.NEW].includes(getEcomOrderStatus(state))

export const getEcomOrderDeliveryDate = (state: any) => getEcomOrder(state).delivery_date
export const getEcomOrderVendorSalesNo = (state: any) => getEcomOrder(state).vendor_sales_order_no
export const getEcomOrderInternalComment = (state: any) => getEcomOrder(state).internal_comment
export const getEcomOrderDealerComment = (state: any) => getEcomOrder(state).dealer_comment
export const getEcomOrderDealerAddress = (state: any) => getEcomOrder(state).address
export const getEcomOrderDealerZip = (state: any) => getEcomOrder(state).zip
export const getEcomOrderDealerCity = (state: any) => getEcomOrder(state).city
export const getEcomOrderIsCustomAddress = (state: any) =>
  Boolean(getEcomOrder(state).is_custom_address)
export const getEcomOrderDeliveryAddressIsPrivate = (state: any) =>
  getEcomOrder(state).delivery_address_is_private
export const getIsLockedForEditing = (state: any) => getEcomOrder(state).isLockedForEditing

export const getSelectedAlias = (state: any) => getState(state).selectedAlias
export const getSmsInputText = (state: any) => getState(state).smsInputText
export const getSelectedReceiver = (state: any) => getState(state).selectedReceiver

export const getEcomOrderUser = (state: any) => {
  const user = {
    id: getEcomOrder(state).created_by_user_id,
    user_name: getEcomOrder(state).created_by,
    country_id: getEcomOrder(state).country_id,
    phone_number: getEcomOrder(state).created_by_phone,
    email: getEcomOrder(state).created_by_email,
  }
  return user.user_name ? user : null
}

export const getEcomOrderContactPerson = (state: any) => {
  const name = getEcomOrder(state).contact_name
  const email = getEcomOrder(state).contact_email
  const phone = getEcomOrder(state).contact_phone_number

  if (!name && !email && !phone) {
    return undefined
  }

  return {
    name,
    email,
    phone,
  }
}

export const getReceiverOptions = (state: any) => {
  const orderUser = getEcomOrderUser(state)
  const productUser = getEcomOrderProductsUser(state)
  const users = []
  if (productUser && productUser.phone_number) {
    users.push({ value: 'end_user', label: productUser.user_name })
  }
  if (orderUser && orderUser.phone_number) {
    users.push({ value: 'order_user', label: orderUser.user_name })
  }

  return users
}

export const getSmsLogForEndUser = (state: any) => {
  const smsLog = getState(state).sms_log || []
  const endUserPhoneNumber = getEcomOrderPhoneNumber(state)
  return smsLog.filter((sms: any) => sms.receiver === String(endUserPhoneNumber)) || []
}

export const getSmsLogForCreatedByUser = (state: any) => {
  const smsLog = getState(state).sms_log || []
  const createdByPhoneNumber = getEcomOrder(state).created_by_phone
  return smsLog.filter((sms: any) => sms.receiver === String(createdByPhoneNumber)) || []
}

export const getEcomOrderProductsUser = (state: any) => {
  // @ts-ignore
  const product = getEcomOrderProducts(state)?.find(product => !!product.user_name?.trim()) || {}
  return product.user_name
    ? {
        user_name: product.user_name,
        department_name: product.department_name,
        department_code: product.department_code,
        accounting_code: product.accounting_code,
        phone_number: product.phone_number,
        email: product.email,
        end_user_id: product.end_user_id,
      }
    : null
}

export const getEcomOrderPhoneNumber = (state: any) => getEcomOrderProductsUser(state)?.phone_number
export const getEcomOrderEndUserId = (state: any) => getEcomOrderProductsUser(state)?.end_user_id

export const getOrderPayload = (state: any) => ({
  delivery_date: getEcomOrderDeliveryDate(state)?.trim() || null,
  dealer_comment: getEcomOrderDealerComment(state),
  internal_comment: getEcomOrderInternalComment(state),
  vendor_sales_order_no: getEcomOrderVendorSalesNo(state),
})
